import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle, FilePdf, ArrowRight, Sparkle, CircleNotch } from "@phosphor-icons/react";
import { api, API_BASE } from "@/lib/api";
import PdfDownloadOverlay from "@/components/PdfDownloadOverlay";

export default function IdeaLabSubmitted() {
  const { ref } = useParams();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/idealab/submissions/${ref}`);
        if (!cancelled) setSub(data);
      } catch (_) {}
      finally { if (!cancelled) setLoading(false); }
    };
    fetchOnce();
    // poll a few times for the AI summary to materialise (background task)
    const intv = setInterval(fetchOnce, 4000);
    setTimeout(() => clearInterval(intv), 30000);
    return () => { cancelled = true; clearInterval(intv); };
  }, [ref]);

  const pdfUrl = `${API_BASE}/idealab/submissions/${ref}/pdf`;
  const summary = sub?.ai_summary;
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfStage, setPdfStage] = useState("preparing"); // preparing | downloading | done

  const downloadPdf = async (e) => {
    e.preventDefault();
    setPdfBusy(true);
    setPdfStage("preparing");
    try {
      const res = await fetch(pdfUrl, { credentials: "include" });
      if (!res.ok) throw new Error("PDF could not be generated");
      setPdfStage("downloading");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Appistry-IdeaLab-${ref}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setPdfStage("done");
      // brief flash of the "done" state so the user sees confirmation
      setTimeout(() => setPdfBusy(false), 900);
    } catch (err) {
      window.open(pdfUrl, "_blank"); // graceful fallback
      setPdfBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="idealab-submitted">
      <PdfDownloadOverlay open={pdfBusy} stage={pdfStage} reference={ref} />
      <header className="bg-white border-b border-zinc-200">
        <div className="app-container py-4 flex items-center justify-between">
          <Link to="/idealab" className="flex items-center gap-2.5">
            <img src="/appistry-logo.png" alt="Appistry" className="h-7 w-7 object-contain" />
            <span className="text-sm font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Appistry IdeaLab</span>
          </Link>
        </div>
      </header>

      <main className="app-container py-16 md:py-24 max-w-3xl">
        <div className="text-center mb-10">
          <CheckCircle size={48} weight="fill" className="text-[hsl(var(--appistry-signal))] mx-auto mb-4" />
          <p className="overline mb-3" data-testid="submitted-reference">Reference · {ref}</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Your idea is in good hands.
          </h1>
          <p className="text-zinc-600 max-w-xl mx-auto">
            We've emailed you a copy of your discovery summary. An Appistry consultant will review your submission and reach out within a few working days.
          </p>
        </div>

        <div className="bg-white border border-zinc-200 p-8 md:p-10">
          <div className="flex flex-col md:flex-row gap-6 md:items-center justify-between">
            <div>
              <div className="overline mb-2">Discovery pack</div>
              <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Appistry-IdeaLab-{ref}.pdf</h2>
              <p className="text-sm text-zinc-500 mt-1">Branded summary of everything you shared — yours to keep.</p>
            </div>
            <a href={pdfUrl} onClick={downloadPdf} className="btn-signal" data-testid="submitted-pdf-cta" aria-busy={pdfBusy}>
              {pdfBusy ? <><CircleNotch size={16} className="animate-spin" /> Preparing PDF…</> : <><FilePdf size={16} weight="fill" /> Download PDF</>}
            </a>
          </div>
        </div>

        {/* AI summary preview (appears once the background task finishes) */}
        <div className="mt-8 bg-white border border-zinc-200 p-8 md:p-10" data-testid="submitted-summary">
          <div className="flex items-center gap-2 mb-3">
            <Sparkle size={18} weight="fill" className="text-[hsl(var(--appistry-signal))]" />
            <div className="overline">AI-assisted discovery summary</div>
          </div>
          {!summary ? (
            <p className="text-zinc-500 text-sm">Generating your discovery summary… it usually takes 10–20 seconds. We've also emailed it to you.</p>
          ) : (
            <div className="space-y-5">
              {summary.business_challenge && (<Block label="Your situation" body={summary.business_challenge} />)}
              {summary.proposed_solution && (<Block label="How we'd shape this" body={summary.proposed_solution} />)}
              {summary.target_users && (<Block label="Who this is for" body={summary.target_users} />)}
              {Array.isArray(summary.key_outcomes) && summary.key_outcomes.length > 0 && (
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">What success looks like</div>
                  <ul className="list-disc list-inside text-zinc-800 space-y-1">
                    {summary.key_outcomes.map((o, i) => <li key={i}>{o}</li>)}
                  </ul>
                </div>
              )}
              {summary.tech_approach && (<Block label="Likely shape of the build" body={summary.tech_approach} />)}
              {summary.what_we_love && (<Block label="What we love about your idea" body={summary.what_we_love} />)}
              {Array.isArray(summary.things_to_explore) && summary.things_to_explore.length > 0 && (
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Questions worth chatting through</div>
                  <ul className="list-disc list-inside text-zinc-800 space-y-1">
                    {summary.things_to_explore.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-10 text-center">
          <Link to="/" className="text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">
            Back to Appistry <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </main>
    </div>
  );
}

function Block({ label, body }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <p className="text-zinc-800 leading-relaxed">{body}</p>
    </div>
  );
}
